// src/pages/admin/reports/ItemsReport.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Card,
  Typography,
  Button,
  CardBody,
  CardFooter,
  CardHeader,
  Input,
  Alert,
  Select,
  Option,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  IconButton,
  Spinner,
  Checkbox,
  Textarea,
} from "@material-tailwind/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import {
  PrinterIcon,
  XMarkIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import ReactToPrint from "react-to-print";

import { collection, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import {
  getDownloadURL,
  uploadBytes,
  deleteObject,
  ref as storageRef,
} from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db, storage } from "../../../firebaseConfig";

/* ✅ ألوان الصورة فقط (Grayscale) */
const BRAND = {
  ink: "#2F2F2F",
  graphite: "#878787",
  silver: "#B8B8B8",
  mist: "#D0D0D0",
  paper: "#EFEFEF",
  white: "#FCFCFC",
};

const ui = {
  pageBg: `radial-gradient(1200px 520px at 90% -30%, ${BRAND.paper}, ${BRAND.white} 55%)`,
  headerBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
  cardBorder: `1px solid ${BRAND.paper}`,
  cardShadow: "0 18px 50px rgba(0,0,0,0.08), 0 6px 18px rgba(0,0,0,0.05)",
  softShadow: "0 10px 26px rgba(0,0,0,0.06)",
  ring: "0 0 0 4px rgba(135,135,135,0.16)",
};

const TABLE_HEAD = [
  "الصورة",
  "الاسم",
  "الوصف",
  "الباركود",
  "الفئة",
  "الكمية",
  "السعر",
  "التاريخ",
  "تعديل",
  "حذف",
];

function truncateText(t, n = 80) {
  if (!t) return "-";
  const s = String(t);
  return s.length > n ? s.slice(0, n) + "…" : s;
}

export default function ItemsReport() {
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // فلاتر
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [lowStockOnly, setLowStockOnly] = useState(false);

  // ترقيم
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // عرض/تعديل/حذف
  const [viewItem, setViewItem] = useState(null);
  const [viewValues, setViewValues] = useState({});
  const [editItem, setEditItem] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const [deleteItem, setDeleteItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // طباعة
  const componentRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) {
        setItems([]);
        setCategories([]);
      }
    });
    return () => unsub();
  }, []);

  async function load() {
    if (!user?.uid) return;
    setLoading(true);
    setError("");
    try {
      const catSnap = await getDocs(collection(db, "categories"));
      const cats = catSnap.docs.map((d) => ({ objectId: d.id, ...(d.data() || {}) }));

      const prodSnap = await getDocs(collection(db, "users", user.uid, "products"));
      const prods = prodSnap.docs.map((d) => ({ objectId: d.id, ...(d.data() || {}) }));

      cats.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ar"));
      setCategories(cats);
      setItems(prods);
    } catch (e) {
      setError("حدث خطأ أثناء جلب البيانات.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!user?.uid) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };
  const handleCategoryChange = (val) => {
    setSelectedCategory(val);
    setCurrentPage(1);
  };

  const CategoryList = useMemo(
    () => [{ objectId: "all", name: "الكل" }, ...categories],
    [categories]
  );

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return items.filter((item) => {
      const matchesSearch =
        (item.name && String(item.name).toLowerCase().includes(q)) ||
        (item.barcode && String(item.barcode).toLowerCase().includes(q)) ||
        (item.description && String(item.description).toLowerCase().includes(q));

      const matchesCategory =
        selectedCategory === "all" ? true : item.categoryId === selectedCategory;

      const matchesLow = lowStockOnly ? Number(item.quantity) < 10 : true;

      return matchesSearch && matchesCategory && matchesLow;
    });
  }, [items, searchQuery, selectedCategory, lowStockOnly]);

  // ✅ مهم: لا تعمل sort على نفس array (بدون mutation)
  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), "ar")
    );
  }, [filteredItems]);

  const pageCount = Math.max(1, Math.ceil(sortedItems.length / itemsPerPage));
  const currentData = sortedItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDate = (val) => {
    if (!val) return "";
    const d = typeof val?.toDate === "function" ? val.toDate() : new Date(val);
    return d.toLocaleDateString("ar-EG");
  };

  // عرض
  const handleViewClick = (item) => {
    const catObj = categories.find((c) => c.objectId === item.categoryId);
    setViewItem(item.objectId);
    setViewValues({
      name: item.name || "",
      barcode: item.barcode || "",
      price: Number(item.price) || 0,
      quantity: Number(item.quantity) || 0,
      categoryName: catObj ? catObj.name : "بدون فئة",
      createdAt: item.createdAt || null,
      description: item.description || "",
      imageURL: item.imageURL || "",
    });
  };

  // تعديل
  const handleEditClick = (item) => {
    setEditItem(item.objectId);
    setEditValues({
      name: item.name || "",
      barcode: item.barcode || "",
      price: item.price || 0,
      quantity: item.quantity || 0,
      categoryId: item.categoryId || "all",
      description: item.description || "",
      imagePath: item.imagePath || null,
      imageURL: item.imageURL || "",
    });
    setEditImageFile(null);
    setEditImagePreview(item.imageURL || "");
  };

  const handleEditChange = (e) =>
    setEditValues((s) => ({ ...s, [e.target.name]: e.target.value }));

  const handleEditImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setEditImageFile(null);
      setEditImagePreview(editValues.imageURL || "");
      return;
    }
    if (!file.type.startsWith("image/")) return;
    if (file.size > 6 * 1024 * 1024) {
      alert("حجم الصورة كبير (الحد 6MB)");
      return;
    }
    setEditImageFile(file);
    setEditImagePreview(URL.createObjectURL(file));
  };

  async function handleEditSubmit() {
    if (!user?.uid || !editItem) return;
    setIsEditing(true);
    try {
      const current = items.find((p) => p.objectId === editItem) || {};
      let newImageURL = current.imageURL || null;
      let newImagePath = current.imagePath || null;

      if (editImageFile) {
        if (current.imagePath) {
          try {
            await deleteObject(storageRef(storage, current.imagePath));
          } catch (_) {}
        }

        const fname =
          (String(editValues.name || "product")
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9\-]/g, "")
            .slice(0, 40) || "product") +
          "-" +
          Date.now();

        const path = `users/${user.uid}/products/${editItem}/${fname}`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, editImageFile, { contentType: editImageFile.type });
        newImageURL = await getDownloadURL(ref);
        newImagePath = path;
      }

      const out = {
        name: (editValues.name || "").trim(),
        barcode: (editValues.barcode || "").trim() || null,
        price: Number(editValues.price) || 0,
        quantity: Number(editValues.quantity) || 0,
        categoryId: editValues.categoryId === "all" ? null : editValues.categoryId,
        description: (editValues.description || "").trim() || null,
        imageURL: newImageURL || null,
        imagePath: newImagePath || null,
      };

      await updateDoc(doc(db, "users", user.uid, "products", editItem), out);

      setItems((prev) => prev.map((p) => (p.objectId === editItem ? { ...p, ...out } : p)));
      setEditItem(null);
    } catch (e) {
      setError("حدث خطأ أثناء التعديل.");
    } finally {
      setIsEditing(false);
    }
  }

  // حذف
  const handleDeleteClick = (item) => setDeleteItem(item);

  async function handleConfirmDelete() {
    if (!user?.uid || !deleteItem) return;
    setIsDeleting(true);
    try {
      if (deleteItem.imagePath) {
        try {
          await deleteObject(storageRef(storage, deleteItem.imagePath));
        } catch (_) {}
      }
      await deleteDoc(doc(db, "users", user.uid, "products", deleteItem.objectId));
      setItems((prev) => prev.filter((p) => p.objectId !== deleteItem.objectId));
      setDeleteItem(null);
    } catch (e) {
      setError("حدث خطأ أثناء الحذف.");
    } finally {
      setIsDeleting(false);
    }
  }

  function handleBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  }

  return (
    <div dir="rtl" className="p-4 min-h-screen" style={{ background: ui.pageBg }}>
      <Card
        className="mx-auto max-w-7xl bg-white rounded-3xl overflow-hidden"
        style={{ border: ui.cardBorder, boxShadow: ui.cardShadow }}
      >
        {/* رأس + رجوع + طباعة */}
        <CardHeader floated={false} shadow={false} className="p-0">
          <div className="flex items-center justify-between px-6 py-6" style={{ background: ui.headerBg }}>
            <button
              onClick={handleBack}
              className="group inline-flex items-center gap-2 rounded-2xl px-3 py-2 transition"
              style={{
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: BRAND.white,
              }}
              title="رجوع"
            >
              <ArrowRightIcon className="h-5 w-5 transition group-hover:translate-x-0.5" />
              <span className="text-sm font-extrabold font-tajawal">رجوع</span>
            </button>

            <div className="text-center flex-1">
              <Typography variant="h4" className="text-white font-extrabold font-tajawal">
                المنتجات
              </Typography>
              <div
                className="mt-2 inline-flex items-center gap-2 rounded-full px-4 py-1 text-xs"
                style={{
                  background: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  color: "rgba(255,255,255,0.85)",
                  fontFamily: "Tajawal, system-ui, sans-serif",
                  fontWeight: 800,
                }}
              >
                <span>عدد العناصر:</span>
                <span>{sortedItems.length}</span>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <ReactToPrint
                trigger={() => (
                  <Button
                    size="sm"
                    className="flex items-center gap-2 font-tajawal rounded-2xl"
                    style={{
                      background: "rgba(255,255,255,0.12)",
                      border: "1px solid rgba(255,255,255,0.18)",
                      color: "white",
                    }}
                  >
                    <PrinterIcon className="h-5 w-5" />
                    طباعة التقرير
                  </Button>
                )}
                content={() => componentRef.current}
              />
            </div>
          </div>
        </CardHeader>

        {/* شريط الأدوات */}
        <div className="px-4 md:px-8 pt-5">
          <div
            className="rounded-2xl p-4"
            style={{ background: BRAND.white, border: `1px solid ${BRAND.paper}`, boxShadow: ui.softShadow }}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-12">
              <div className="relative lg:col-span-6 sm:col-span-2 col-span-1">
                <Input
                  type="text"
                  placeholder="ابحث بالاسم أو الباركود أو الوصف..."
                  size="lg"
                  icon={<MagnifyingGlassIcon className="h-5 w-5 text-blue-gray-500" />}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="text-right font-tajawal"
                  labelProps={{ className: "before:content-none after:content-none" }}
                  containerProps={{ className: "min-w-0" }}
                  crossOrigin=""
                />
              </div>

              <div className="lg:col-span-3 col-span-1">
                <Select
                  className="font-tajawal"
                  labelProps={{ className: "font-tajawal" }}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  label="اختر الفئة"
                  size="lg"
                >
                  {CategoryList.map((cat) => (
                    <Option className="font-tajawal" key={cat.objectId} value={cat.objectId}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="lg:col-span-3 col-span-1">
                <div
                  className="h-full rounded-2xl border px-3 py-2 flex items-center"
                  style={{ borderColor: BRAND.paper, backgroundColor: BRAND.white }}
                >
                  <Checkbox
                    labelProps={{ className: "font-tajawal" }}
                    color="blue-gray"
                    label="أوشكت على النفاد (أقل من 10)"
                    ripple={false}
                    checked={lowStockOnly}
                    onChange={(e) => setLowStockOnly(e.target.checked)}
                  />
                  <div className="ms-auto">
                    <Button
                      variant="outlined"
                      onClick={load}
                      disabled={loading}
                      className="flex items-center gap-2 font-tajawal rounded-2xl"
                      style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                    >
                      <ArrowPathIcon className="h-5 w-5" />
                      تحديث
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* تنبيهات */}
            <div className="mt-4">
              {loading && (
                <Alert color="blue" className="text-center font-tajawal">
                  جاري التحميل...
                </Alert>
              )}
              {error && (
                <Alert color="red" className="text-center font-tajawal">
                  {error}
                </Alert>
              )}
              {!user && (
                <Alert color="blue-gray" className="text-center font-tajawal">
                  يرجى تسجيل الدخول لعرض منتجاتك.
                </Alert>
              )}
            </div>
          </div>
        </div>

        {user && (
          <>
            <CardBody className="overflow-auto px-4 md:px-8 pb-4 pt-4">
              <div className="overflow-auto rounded-2xl" style={{ border: `1px solid ${BRAND.paper}`, boxShadow: ui.softShadow }}>
                <table className="w-full min-w-max text-right border-collapse font-tajawal">
                  <thead>
                    <tr style={{ backgroundColor: BRAND.paper }}>
                      {TABLE_HEAD.map((head) => (
                        <th
                          key={head}
                          className="p-3 text-sm text-center font-extrabold"
                          style={{ color: BRAND.ink, borderBottom: `1px solid ${BRAND.mist}` }}
                        >
                          {head}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {currentData.length ? (
                      currentData.map((item, index) => {
                        const catObj = categories.find((c) => c.objectId === item.categoryId);
                        return (
                          <tr
                            key={item.objectId}
                            className="transition-colors"
                            style={{
                              background: index % 2 === 0 ? BRAND.white : "#FFFFFF",
                              borderTop: `1px solid ${BRAND.paper}`,
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(239,239,239,0.65)")}
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = index % 2 === 0 ? BRAND.white : "#FFFFFF")
                            }
                          >
                            {/* الصورة */}
                            <td className="p-3 text-center" style={{ borderColor: BRAND.paper }}>
                              {item.imageURL ? (
                                <img
                                  onClick={() => handleViewClick(item)}
                                  src={item.imageURL}
                                  alt={item.name || ""}
                                  className="h-14 w-14 inline-block rounded-xl object-cover ring-1 ring-black/5 cursor-pointer"
                                />
                              ) : (
                                <span className="text-xs" style={{ color: BRAND.graphite }}>بدون صورة</span>
                              )}
                            </td>

                            {/* الاسم */}
                            <td
                              className="p-3 text-center text-sm font-extrabold"
                              style={{ color: BRAND.ink, cursor: "pointer" }}
                              onClick={() => handleViewClick(item)}
                              title="عرض التفاصيل"
                            >
                              {item.name || ""}
                            </td>

                            {/* الوصف */}
                            <td
                              className="p-3 text-center text-sm"
                              style={{ color: BRAND.graphite, maxWidth: 320 }}
                              title={item.description || ""}
                            >
                              {truncateText(item.description, 90)}
                            </td>

                            {/* الباركود */}
                            <td className="p-3 text-center text-sm" style={{ color: BRAND.ink }}>
                              {item.barcode || "-"}
                            </td>

                            {/* الفئة */}
                            <td className="p-3 text-center text-sm" style={{ color: BRAND.ink }}>
                              {catObj ? catObj.name : "بدون فئة"}
                            </td>

                            {/* الكمية */}
                            <td className="p-3 text-center text-sm" style={{ color: BRAND.ink }}>
                              {Number(item.quantity || 0)}
                            </td>

                            {/* السعر */}
                            <td className="p-3 text-center text-sm" style={{ color: BRAND.ink, fontWeight: 900 }}>
                              {Number(item.price || 0).toLocaleString("en-EG")}
                            </td>

                            {/* التاريخ */}
                            <td className="p-3 text-center text-sm" style={{ color: BRAND.graphite }}>
                              {item.createdAt ? formatDate(item.createdAt) : ""}
                            </td>

                            {/* تعديل */}
                            <td className="p-3 text-center">
                              <Button
                                className="font-tajawal rounded-2xl"
                                variant="outlined"
                                size="sm"
                                onClick={() => handleEditClick(item)}
                                style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                              >
                                تعديل
                              </Button>
                            </td>

                            {/* حذف */}
                            <td className="p-3 text-center">
                              <Button
                                className="font-tajawal rounded-2xl"
                                size="sm"
                                onClick={() => handleDeleteClick(item)}
                                style={{ background: `linear-gradient(135deg, #7f1d1d, #991b1b)`, color: "white" }}
                              >
                                حذف
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={TABLE_HEAD.length} className="p-6 text-center" style={{ color: BRAND.graphite }}>
                          لا توجد بيانات متاحة.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>

            <CardFooter className="flex flex-col items-center justify-between gap-4 p-6 md:flex-row">
              <Typography className="font-tajawal" style={{ color: BRAND.graphite }}>
                صفحة {currentPage} من {pageCount}
              </Typography>

              <div className="flex gap-2">
                <IconButton
                  variant="outlined"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  title="السابق"
                  style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                >
                  <ArrowRightIcon className="h-5 w-5" />
                </IconButton>

                <IconButton
                  variant="outlined"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(pageCount, p + 1))}
                  disabled={currentPage === pageCount}
                  title="التالي"
                  style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                </IconButton>
              </div>
            </CardFooter>
          </>
        )}
      </Card>

      {/* عنصر الطباعة */}
      <div className="hidden">
        <PrintableItemsTable ref={componentRef} items={sortedItems} categories={categories} brand={BRAND} />
      </div>

      {/* عرض */}
      <Dialog open={!!viewItem} handler={() => setViewItem(null)} size="lg">
        <DialogHeader className="justify-between">
          <Typography className="font-tajawal" variant="h5" style={{ color: BRAND.ink }}>
            تفاصيل المنتج
          </Typography>
          <IconButton variant="text" color="blue-gray" onClick={() => setViewItem(null)}>
            <XMarkIcon className="h-6 w-6" />
          </IconButton>
        </DialogHeader>

        <DialogBody divider className="max-h-[75vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
            <div className="flex flex-col gap-2">
              <Info label="الاسم" value={viewValues.name || "-"} brand={BRAND} />
              <Info label="الباركود" value={viewValues.barcode || "-"} brand={BRAND} />
              <Info label="الفئة" value={viewValues.categoryName || "بدون فئة"} brand={BRAND} />
              <Info
                label="تاريخ الإنشاء"
                value={viewValues.createdAt ? formatDate(viewValues.createdAt) : "-"}
                brand={BRAND}
              />
              <Info
                label="السعر"
                value={`${Number(viewValues.price || 0).toLocaleString("en-EG")} د.ع`}
                brand={BRAND}
              />
              <Info label="الكمية" value={viewValues.quantity || 0} brand={BRAND} />
            </div>

            <div className="flex flex-col gap-2">
              <Typography variant="small" className="uppercase mb-1" style={{ color: BRAND.graphite }}>
                الصورة
              </Typography>
              {viewValues.imageURL ? (
                <img
                  src={viewValues.imageURL}
                  alt={viewValues.name || ""}
                  className="w-full max-w-sm rounded-2xl object-cover ring-1 ring-black/5"
                />
              ) : (
                <div className="px-3 py-2 rounded-2xl" style={{ background: BRAND.paper, border: `1px solid ${BRAND.mist}` }}>
                  <Typography className="text-sm" style={{ color: BRAND.graphite }}>
                    لا توجد صورة
                  </Typography>
                </div>
              )}

              <Typography variant="small" className="uppercase mt-3" style={{ color: BRAND.graphite }}>
                الوصف
              </Typography>
              <div className="px-3 py-2 rounded-2xl" style={{ background: BRAND.paper, border: `1px solid ${BRAND.mist}` }}>
                <Typography className="text-sm whitespace-pre-wrap" style={{ color: BRAND.ink }}>
                  {viewValues.description || "-"}
                </Typography>
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter className="justify-end gap-2">
          <Button
            className="font-tajawal"
            variant="text"
            onClick={() => setViewItem(null)}
            style={{ color: BRAND.graphite }}
          >
            إغلاق
          </Button>
        </DialogFooter>
      </Dialog>

      {/* تعديل */}
      <Dialog dir="rtl" open={!!editItem} handler={() => setEditItem(null)} size="lg">
        <DialogHeader className="justify-between">
          <Typography className="font-tajawal" variant="h5" style={{ color: BRAND.ink }}>
            تعديل المنتج
          </Typography>
          <IconButton variant="text" color="blue-gray" onClick={() => setEditItem(null)}>
            <XMarkIcon className="h-6 w-6" />
          </IconButton>
        </DialogHeader>

        <DialogBody divider className="max-h-[75vh] overflow-y-auto">
          <div className="space-y-6">
            <Section>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  labelProps={{ className: "font-tajawal" }}
                  className="font-tajawal"
                  label="الاسم"
                  name="name"
                  value={editValues.name || ""}
                  onChange={handleEditChange}
                  crossOrigin=""
                />
                <Input
                  labelProps={{ className: "font-tajawal" }}
                  className="font-tajawal"
                  label="الباركود"
                  name="barcode"
                  value={editValues.barcode || ""}
                  onChange={handleEditChange}
                  crossOrigin=""
                />
                <Input
                  labelProps={{ className: "font-tajawal" }}
                  className="font-tajawal"
                  label="السعر"
                  name="price"
                  value={editValues.price || ""}
                  onChange={handleEditChange}
                  crossOrigin=""
                />
                <Input
                  labelProps={{ className: "font-tajawal" }}
                  className="font-tajawal"
                  label="الكمية"
                  name="quantity"
                  value={editValues.quantity || ""}
                  onChange={handleEditChange}
                  crossOrigin=""
                />

                <Select
                  labelProps={{ className: "font-tajawal" }}
                  className="font-tajawal"
                  label="الفئة"
                  size="md"
                  value={editValues.categoryId || "all"}
                  onChange={(val) => setEditValues((s) => ({ ...s, categoryId: val }))}
                >
                  {CategoryList.filter((c) => c.objectId !== "all").map((cat) => (
                    <Option className="font-tajawal" key={cat.objectId} value={cat.objectId}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </div>
            </Section>

            <Section>
              <Textarea
                label="الوصف"
                name="description"
                value={editValues.description || ""}
                onChange={handleEditChange}
                className="font-tajawal"
                labelProps={{ className: "font-tajawal" }}
              />
            </Section>

            <Section>
              <Typography variant="small" className="font-tajawal" style={{ color: BRAND.graphite }}>
                صورة المنتج (اختياري)
              </Typography>

              <div className="flex items-start gap-4">
                <div>
                  {editImagePreview ? (
                    <img
                      src={editImagePreview}
                      alt="معاينة"
                      className="h-28 w-28 rounded-2xl object-cover ring-1 ring-black/5"
                    />
                  ) : (
                    <div
                      className="h-28 w-28 rounded-2xl grid place-items-center text-sm"
                      style={{ background: BRAND.paper, color: BRAND.graphite, border: `1px solid ${BRAND.mist}` }}
                    >
                      لا صورة
                    </div>
                  )}
                </div>

                <div className="flex-1">
                  <input type="file" accept="image/*" onChange={handleEditImageChange} className="block w-full text-sm" />
                  <Typography variant="small" className="mt-1" style={{ color: BRAND.graphite }}>
                    يُفضّل ألا تتجاوز 6MB.
                  </Typography>
                </div>
              </div>
            </Section>
          </div>
        </DialogBody>

        <DialogFooter className="justify-end gap-2">
          <Button
            className="font-tajawal"
            variant="text"
            onClick={() => setEditItem(null)}
            disabled={isEditing}
            style={{ color: BRAND.graphite }}
          >
            إلغاء
          </Button>

          <Button
            className="font-tajawal rounded-2xl"
            onClick={handleEditSubmit}
            disabled={isEditing}
            style={{ background: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`, color: "white" }}
          >
            {isEditing && <Spinner className="me-2" />} حفظ التعديلات
          </Button>
        </DialogFooter>
      </Dialog>

      {/* حذف */}
      <Dialog dir="rtl" open={!!deleteItem} handler={() => !isDeleting && setDeleteItem(null)} size="sm">
        <DialogHeader className="justify-between">
          <Typography className="font-tajawal" variant="h6" style={{ color: BRAND.ink }}>
            تأكيد الحذف
          </Typography>
          <IconButton variant="text" color="blue-gray" onClick={() => setDeleteItem(null)}>
            <XMarkIcon className="h-6 w-6" />
          </IconButton>
        </DialogHeader>

        <DialogBody divider>
          <Typography className="text-sm font-tajawal" style={{ color: BRAND.ink }}>
            هل تريد حذف المنتج <span className="font-semibold">{deleteItem?.name || ""}</span>؟
          </Typography>
          {deleteItem?.barcode && (
            <Typography variant="small" className="mt-2 font-tajawal" style={{ color: BRAND.graphite }}>
              الباركود: {deleteItem.barcode}
            </Typography>
          )}
        </DialogBody>

        <DialogFooter className="justify-end gap-2">
          <Button
            className="font-tajawal"
            variant="text"
            onClick={() => setDeleteItem(null)}
            disabled={isDeleting}
            style={{ color: BRAND.graphite }}
          >
            إلغاء
          </Button>

          <Button
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            className="flex items-center gap-2 font-tajawal rounded-2xl"
            style={{ background: `linear-gradient(135deg, #7f1d1d, #991b1b)`, color: "white" }}
          >
            {isDeleting && <Spinner size="sm" />} حذف
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

/* عناصر مساعدة */
function Section({ title, children }) {
  return (
    <div className="pb-4 border-b last:border-b-0 space-y-2" style={{ borderColor: BRAND.paper }}>
      {title && (
        <Typography variant="small" className="font-semibold mb-2" style={{ color: BRAND.graphite }}>
          {title}
        </Typography>
      )}
      {children}
    </div>
  );
}

function Info({ label, value, brand }) {
  return (
    <div className="flex flex-col">
      <Typography variant="small" className="uppercase mb-1" style={{ color: brand.graphite }}>
        {label}
      </Typography>
      <div className="px-3 py-2 rounded-2xl break-words" style={{ background: brand.paper, border: `1px solid ${brand.mist}` }}>
        <Typography className="text-sm" style={{ color: brand.ink }}>
          {String(value)}
        </Typography>
      </div>
    </div>
  );
}

const PrintableItemsTable = React.forwardRef(function PrintableItemsTable({ items, categories, brand }, ref) {
  const catName = (id) => categories.find((c) => c.objectId === id)?.name || "بدون فئة";
  return (
    <div dir="rtl" ref={ref} className="p-6" style={{ color: brand.ink, fontFamily: "Tajawal, system-ui, sans-serif" }}>
      <h1 className="text-xl font-extrabold mb-4">تقرير المنتجات</h1>
      <table className="w-full text-right border" style={{ borderColor: brand.mist }}>
        <thead>
          <tr style={{ background: brand.paper }}>
            <th className="p-2 border" style={{ borderColor: brand.mist }}>الصورة</th>
            <th className="p-2 border" style={{ borderColor: brand.mist }}>الاسم</th>
            <th className="p-2 border" style={{ borderColor: brand.mist }}>الوصف</th>
            <th className="p-2 border" style={{ borderColor: brand.mist }}>الباركود</th>
            <th className="p-2 border" style={{ borderColor: brand.mist }}>الفئة</th>
            <th className="p-2 border" style={{ borderColor: brand.mist }}>الكمية</th>
            <th className="p-2 border" style={{ borderColor: brand.mist }}>السعر</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.objectId}>
              <td className="p-2 border" style={{ borderColor: brand.mist }}>
                {r.imageURL ? (
                  <img
                    src={r.imageURL}
                    alt={r.name || ""}
                    style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 10 }}
                  />
                ) : (
                  "-"
                )}
              </td>
              <td className="p-2 border" style={{ borderColor: brand.mist }}>{r.name}</td>
              <td className="p-2 border" style={{ borderColor: brand.mist }}>{truncateText(r.description, 90)}</td>
              <td className="p-2 border" style={{ borderColor: brand.mist }}>{r.barcode || "-"}</td>
              <td className="p-2 border" style={{ borderColor: brand.mist }}>{catName(r.categoryId)}</td>
              <td className="p-2 border" style={{ borderColor: brand.mist }}>{Number(r.quantity || 0)}</td>
              <td className="p-2 border" style={{ borderColor: brand.mist }}>{Number(r.price || 0).toLocaleString("en-EG")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});
